import React, { useEffect, useState } from 'react';
import ImageWithPreview from './common/MarkdownImage';

/** 会话图片读取器：输入原始文件地址和取消信号，返回可供 img 使用的图片数据 URL。 */
export type MarkdownImageLoader = (src: string, signal: AbortSignal) => Promise<string>;

/** 区分文件引用和网络资源，避免将登录凭证发送到模型返回的外部 URL。 */
export function isLocalMarkdownImage(src: string): boolean {
  if (!src || src.startsWith('//') || /^\/(?:api|assets)\//i.test(src)) return false;
  return /^file:\/\//i.test(src) || /^[a-z]:[\\/]/i.test(src) || !/^[a-z][a-z\d+.-]*:/i.test(src);
}

/** 图片读取所需的会话范围；桌面文件通过现有 Relay 通道读取。 */
interface SessionImageOptions {
  sessionId: string;
  sandbox: boolean;
  cwd?: string;
  getHeaders: () => Record<string, string>;
  readDesktopImage?: (path: string) => Promise<{ content?: string; mimeType?: string }>;
}

/** 创建会话隔离的读取器；保留沙箱绝对路径，不能套用宿主机 cwd 映射。 */
export function createSessionImageLoader(options: SessionImageOptions): MarkdownImageLoader {
  return async (src, signal) => {
    if (!isLocalMarkdownImage(src)) throw new Error('不是会话文件地址');
    let path = src;
    if (/^file:\/\//i.test(path)) {
      const url = new URL(path);
      if (url.hostname && url.hostname !== 'localhost') throw new Error('不支持远程文件地址');
      path = url.pathname.replace(/^\/([a-z]:\/)/i, '$1');
    }
    try { path = decodeURIComponent(path); } catch { /* 保留未编码或包含字面百分号的文件名。 */ }
    if (!path.startsWith('/') && !/^[a-z]:[\\/]/i.test(path)) {
      const root = options.sandbox && !options.readDesktopImage ? '/workspace/project' : options.cwd;
      if (!root) throw new Error('缺少会话工作目录');
      path = `${root.replace(/[\\/]+$/, '')}/${path.replace(/^\.\//, '')}`;
    }
    signal.throwIfAborted();
    if (options.readDesktopImage) {
      const image = await options.readDesktopImage(path);
      signal.throwIfAborted();
      if (!image.content || !/^image\/(?:png|jpe?g|webp|gif|avif|bmp)$/i.test(image.mimeType ?? '')) {
        throw new Error('桌面未返回有效图片');
      }
      return `data:${image.mimeType};base64,${image.content}`;
    }
    const endpoint = options.sandbox
      ? `/api/cowork/sessions/${encodeURIComponent(options.sessionId)}/files/read`
      : '/api/files/read';
    const response = await fetch(`${endpoint}?path=${encodeURIComponent(path)}`, {
      headers: options.getHeaders(), signal,
    });
    if (!response.ok) throw new Error(response.status === 409 ? '沙箱暂不可读，请恢复会话后重试' : '图片读取失败');
    const image = await response.json();
    if (image.type !== 'image' || typeof image.dataUrl !== 'string' || !/^data:image\/(?:png|jpe?g|webp|gif|avif|bmp);base64,/i.test(image.dataUrl)) {
      throw new Error('文件接口未返回有效图片');
    }
    return image.dataUrl;
  };
}

/** Markdown 图片：本地文件异步鉴权读取；外部图片维持浏览器原有加载方式。 */
export default function MarkdownImage({ src = '', alt = '', loadImage, ...props }: React.ImgHTMLAttributes<HTMLImageElement> & {
  loadImage?: MarkdownImageLoader;
}) {
  const local = !!loadImage && isLocalMarkdownImage(src);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ src: string; loader: MarkdownImageLoader; url?: string; error?: string }>();
  useEffect(() => {
    if (!local || !loadImage) return;
    const controller = new AbortController();
    setResult(undefined);
    loadImage(src, controller.signal).then(url => {
      if (!controller.signal.aborted) setResult({ src, loader: loadImage, url });
    }).catch(error => {
      if (!controller.signal.aborted) setResult({ src, loader: loadImage, error: error instanceof Error ? error.message : '图片加载失败' });
    });
    return () => controller.abort();
  }, [src, loadImage, local, attempt]);

  if (!local) return <ImageWithPreview src={src || undefined} alt={alt} title={props.title} />;
  // 切换会话的首帧也不能显示上一会话的图片，不能只依赖 effect 清空状态。
  const current = result?.src === src && result.loader === loadImage ? result : undefined;
  if (current?.url) return <ImageWithPreview src={current.url} alt={alt} title={props.title} />;
  if (current?.error) return <span role="status" className="inline-flex items-center gap-2 text-sm text-gray-500">
    {alt || '图片'}：{current.error}
    <button type="button" className="text-claude-accent underline" onClick={() => setAttempt(value => value + 1)}>重新加载</button>
  </span>;
  return <span role="status" className="inline-block animate-pulse text-sm text-gray-500">正在加载{alt || '图片'}…</span>;
}
